import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Stack, Typography, CircularProgress, Dialog, DialogTitle, DialogContent, Grid, Drawer } from '@mui/material';
import { styled } from '@mui/material/styles';
import uploadImg from '../assets/upload-img.png';
import { updateResumeUrl, getResumeUrl, addCandidateResume, uploadResumeInResume } from 'api/users';
import { useSnackbar } from 'components/snackbar';
import { Close as CloseIcon } from "@mui/icons-material";
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { useMediaQuery, useTheme } from "@mui/material";
import { useBackNavigationDialog } from 'hooks/useBackNavigationDialog';

const PreviewResume = `${process.env.REACT_APP_HOMEPAGE}assets/resume_thumbnail_2.jpg`;

const UploadButton = styled(Button)({
    position: 'relative',
    padding: 0,
    width: '50px',
    height: '50px',
    '&:hover .overlay': {
        opacity: 1,
    },
});

const Overlay = styled('div')({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    color: 'white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    fontSize: '12px',
    textAlign: 'center',
});

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

/**
 * Component to upload and display a user's resume.
 * @param {function} onProfilePhotoChange - Function to call when the user's resume is updated.
 * @param {function} setResume - Function to set the resume URL in state.
 * @param {boolean} isResume - Whether the component is being used for a resume or job application.
 * @param {function} handleUploadResume - Function to call when the user uploads a new resume.
 * @param {string} initialResumeUrl - The initial resume URL to display. If not provided, the component will fetch the resume URL from the server.
 * @returns {JSX.Element} - The rendered component.
 */
const UploadExistingProfile = ({ onProfilePhotoChange, setResume, isResume, handleUploadResume, initialResumeUrl }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(false);
    // const [openDialog, setOpenDialog] = useState(false);
    const [resumeLink, setResumeLink] = useState(null);
    const fileInputRef = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        if (initialResumeUrl) {
            setResumeLink(initialResumeUrl);
            setResume(initialResumeUrl);
        }
    }, [initialResumeUrl, setResume]);

    /**
     * Fetches the user's resume URL from the server when the component mounts.
     * If the response is successful and the resume URL is present, it sets the resume
     * URL in component state and calls the `setResume` function to update the parent
     * component's state.
     * If an error occurs, it logs an error message to the console.
     */
    const fetchResumeOnMount = async () => {
        try {
            const response = await getResumeUrl();
            if (response?.success && response.data) {
                const resumeUrl = response.data ? process.env.REACT_APP_END_POINT + response.data : '';
                setResumeLink(resumeUrl);
                setResume(resumeUrl);
            }
        } catch (error) {
            console.error("Error fetching initial resume:", error);
        }
    };

    useEffect(() => {

        if (!initialResumeUrl) {
            fetchResumeOnMount();
        }
    }, []);

    /**
     * Fetches the user's resume URL from the server and sets the resume
     * URL in component state. If an error occurs, it logs an error message to
     * the console and shows a snackbar with the error message.
     */
    const fetchResume = async () => {
        try {
            const response = await getResumeUrl();
            if (response?.success) {
                const resumeUrl = response.data ? process.env.REACT_APP_END_POINT + response.data : '';
                setResumeLink(resumeUrl);
                setResume(resumeUrl);
            }
        } catch (error) {
            console.error("Error fetching resume:", error);
            enqueueSnackbar("An error occurred while loading resume", { variant: "error" });
        }
    };
    /**
     * Handles file input change by validating and uploading the selected file.
     * Displays error messages if the file size exceeds 2MB or if the file type is not
     * among the allowed types (PDF, DOC, DOCX). If file validation passes, uploads the
     * file using different APIs based on the context (resume or profile photo).
     * Updates the component state with the new resume URL and displays success or error
     * messages based on the upload outcome.
     * 
     * @param {Event} e - The file input change event.
     */

    const handleFileChange = async (e) => {
        if (!e.target.files || e.target.files.length === 0) return;

        const file = e.target.files[0];

        // Restrict file size to 2MB
        if (file.size > 2 * 1024 * 1024) {
            enqueueSnackbar("File size must be less than 2 MB. Please upload a smaller file", { variant: "error" });
            return;
        }
        const allowedTypes = [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ];

        if (!allowedTypes.includes(file.type)) {
            enqueueSnackbar("Invalid file type. Only PDF, DOC, and DOCX files are allowed", { variant: "error" });
            return;
        }

        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append("file", file);

            let response;
            let resumeUrl;
            if (isResume) {
                const url = await uploadResumeInResume(formData);
                setResumeLink(process.env.REACT_APP_END_POINT + url?.data?.resumeUrl);
                handleUploadResume(url?.data);
                return;
            }
            if (resumeLink) {
                response = await updateResumeUrl(formData);
                if (response?.success && response?.data) {
                    resumeUrl = response?.data;
                } else {
                    enqueueSnackbar("Failed to update resume", { variant: "error" });
                    return;
                }
            } else {
                response = await addCandidateResume(formData);
                if (response?.success && response?.data?.resumeUrl) {
                    resumeUrl = response?.data?.resumeUrl;
                    enqueueSnackbar("Resume upload successfully", { variant: "success" });
                } else {
                    enqueueSnackbar("Failed to update resume", { variant: "error" });
                    return;
                }
            }

            if (response.success) {
                const resumeLink = process.env.REACT_APP_END_POINT + resumeUrl;
                onProfilePhotoChange(true);
                setResumeLink(resumeLink);
                setResume(resumeLink);
                handlePreviewResumeClose();
            } else {
                enqueueSnackbar(response.message || "Upload failed", { variant: "error" });
            }
        } catch (error) {
            console.error("Error handling resume upload:", error);
            enqueueSnackbar("An error occurred while uploading", { variant: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    const { openDialog, closeDialog, dialogLocation } = useBackNavigationDialog();
    const dialogIds = dialogLocation.state?.dialogIds || [];

    const previewResume = dialogIds.includes("uploadPreviewResume");
    const handlePreviewResumeOpen = () => {
        openDialog('uploadPreviewResume');
    };
    const handlePreviewResumeClose = () => {
        closeDialog('uploadPreviewResume');
    };

    return (
        <Box>
            <Stack
                className="upload-photo-stack-wrapper"
                direction={{ xs: "column", sm: "row" }}
                justifyContent={"space-between"}
                alignItems={"center"}
                spacing={{ xs: "20px", md: 0 }}
            >
                <Box className="upload-profile-photo-info">
                    <Typography className="step-heading xs-fs-16" variant="h6">
                        Your Uploaded Resume
                    </Typography>
                    {!isResume && <Typography className="step-info" variant="body2" gutterBottom>
                        This resume will be shared with recruiters for your job applications.
                    </Typography>}
                </Box>

                {/* Resume Thumbnail */}
                <Box className="upload-photo-wrapper">
                    <UploadButton component="label" variant="contained" className="upload-profile-btn">
                        <img
                            alt="resume"
                            src={resumeLink ? PreviewResume : uploadImg}
                            onError={(e) => (e.target.src = uploadImg)}
                            className="select-resume-option-container"
                            onClick={() => handlePreviewResumeOpen()}
                        />
                    </UploadButton>
                </Box>
            </Stack>
            <input type="file"
                accept=".pdf, .doc, .docx, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />
            {/* Full-Screen Mobile Preview */}
            {isMobile ? (
                <Drawer open={previewResume} anchor={'right'}
                    fullScreen
                    className='preview-resume-dialog'
                    sx={{ "& .MuiDrawer-paper": { width: "100%" } }}
                >
                    <Box sx={{ width: { xs: '100%', md: '450px' } }} className="job-app-drawer-container" role="presentation" >
                        <Box className="drawer-header">
                            <Stack direction="row" alignItems={'flex-start'} spacing={1}>
                                <Box>
                                    <Typography className="title">Uploaded Resume</Typography>
                                </Box>
                            </Stack>
                        </Box>
                        <Box className="drawer-content" padding={'0px !important'}>
                            <Box className="my-resume-overview" >
                                {isLoading ? (
                                    <Stack justifyContent="center" alignItems="center" >
                                        <CircularProgress size={40} />
                                    </Stack>
                                ) : resumeLink ? (
                                    <>
                                        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js" />
                                        <div style={{ width: '100%' }}>
                                            <Viewer fileUrl={resumeLink}
                                                defaultScale={.628}
                                                style={{ width: '100%' }} />
                                        </div>
                                    </>
                                ) : (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            height: "100%",
                                            width: "100%",
                                            border: "2px dashed #ccc",
                                            borderRadius: "8px",
                                            cursor: "pointer",
                                            textAlign: "center",
                                            padding: "20px"
                                        }}
                                        onClick={() => fileInputRef.current.click()}
                                    >
                                        <Typography variant="body1" color="textSecondary">No resume uploaded</Typography>
                                        <Button
                                            variant="contained"
                                            sx={{ mt: 2, backgroundColor: "#1575C6", "&:hover": { backgroundColor: "#0056b3" } }}
                                        >
                                            Please upload in PDF (preferred) or word format
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                        <Box className="drawer-footer">
                            <Stack direction="row" spacing={2}>
                                <Button fullWidth size="large" className="cancel-btn"
                                    onClick={() => fileInputRef.current.click()}
                                    disabled={isLoading || !resumeLink}>
                                    Replace
                                </Button>
                                <Button fullWidth variant="contained" size="large" className="update-btn"
                                    onClick={() => handlePreviewResumeClose()}
                                    disabled={isLoading || !resumeLink}>
                                    Continue
                                </Button>
                            </Stack>
                        </Box>
                    </Box>
                </Drawer>
            ) : (
                /* Desktop Dialog */
                <Dialog open={previewResume} onClose={() => handlePreviewResumeClose()} sx={{ maxWidth: "600px", margin: "auto", zIndex: 1300 }} fullWidth>
                    <DialogTitle sx={{ padding: "12px 20px", borderBottom: "1px solid #ddd" }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>Uploaded Resume</Typography>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => fileInputRef.current.click()}
                                    disabled={isLoading || !resumeLink}
                                    sx={{ color: "#1575C6", textTransform: "none", fontWeight: 500 }}
                                >
                                    Replace
                                </Button>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => handlePreviewResumeClose()}
                                    disabled={isLoading || !resumeLink}
                                    sx={{
                                        backgroundColor: "#1575C6",
                                        color: "white",
                                        textTransform: "none",
                                        fontWeight: 500,
                                        "&:hover": { backgroundColor: "#0056b3" },
                                    }}
                                >
                                    Continue
                                </Button>
                            </Stack>
                        </Stack>
                    </DialogTitle>

                    {/* PDF Viewer */}
                    <DialogContent sx={{ padding: "0 !important", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <Box
                            className="resume-options-preview"
                            sx={{
                                height: "75vh",
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                borderRadius: "8px",
                            }}
                        >
                            {isLoading ? (
                                <Stack justifyContent="center" alignItems="center">
                                    <CircularProgress size={40} />
                                </Stack>
                            ) : resumeLink ? (
                                <>
                                    <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js" />
                                    <Box sx={{ width: "100%", height: "100%" }}>
                                        <div style={{ width: '100%' }}>
                                            <Viewer fileUrl={resumeLink}
                                                defaultScale={.874}
                                                style={{ width: '100%' }} />
                                        </div>
                                    </Box>
                                </>
                            ) : (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        height: "100%",
                                        width: "100%",
                                        border: "2px dashed #ccc",
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                        textAlign: "center",
                                        padding: "20px"
                                    }}
                                    onClick={() => fileInputRef.current.click()}
                                >
                                    <Typography variant="body1" color="textSecondary">No resume uploaded</Typography>
                                    <Button
                                        variant="contained"
                                        sx={{ mt: 2, backgroundColor: "#1575C6", "&:hover": { backgroundColor: "#0056b3" } }}
                                    >
                                        Please upload in PDF (preferred) or word format
                                    </Button>
                                </Box>
                            )}

                        </Box>

                    </DialogContent>
                </Dialog>
            )}
        </Box>
    );
};

export default UploadExistingProfile;